import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BubbleDto, SavedChatDto, SavedChatsMap } from './savedChatsDto';
import { RootState } from '../store';

interface SavedChatsState {
  savedChatsDto: SavedChatDto[];
  savedChats: SavedChatsMap;
  selectedChat: SavedChatDto;
  chatBubbles: BubbleDto[];
  ctFeedback: string;
  supervisorNotes: string;
  filter: string;
}

const initialState: SavedChatsState = {
  savedChatsDto: [],
  savedChats: {},
  selectedChat: {
    id: '',
    created_at: '',
    updated_at: '',
    chatId: '',
    owner: '',
    chatName: '',
    feedback: '',
    reviewer: '',
    status: '',
    stage: '',
    notes: '',
  },
  chatBubbles: [],
  ctFeedback: '',
  filter: '',
  supervisorNotes: '',
};

const filterTbl = (chats: SavedChatDto[], filter: string) => {
  let filtered: SavedChatDto[] = [];
  chats.forEach((chat) => {
    if (chat.status === filter) {
      filtered.push(chat);
    }
  });

  return filtered;
};

export const savedChatsSlice = createSlice({
  name: 'savedChats',
  initialState,
  reducers: {
    setSavedChats: (state, action: PayloadAction<SavedChatDto[]>) => {
      if (action.payload.length !== 0) {
        state.savedChatsDto = action.payload;
      }

      const savedChats: SavedChatsMap = {};
      if (state.filter && state.filter !== 'none') {
        const filteredTbl = filterTbl(state.savedChatsDto, state.filter);
        for (const savedChat of filteredTbl) {
          savedChats[savedChat.id] = savedChat;
        }
      } else {
        for (const savedChat of state.savedChatsDto) {
          savedChats[savedChat.id] = savedChat;
        }
      }
      state.savedChats = savedChats;
    },
    setSelectedChat: (state, action: PayloadAction<SavedChatDto>) => {
      state.selectedChat = action.payload;
    },
    setChatBubbles: (state, action: PayloadAction<BubbleDto[]>) => {
      action.payload.sort((a, b) => {
        return (
          new Date(a.original_created_at).getTime() -
          new Date(b.original_created_at).getTime()
        );
      });
      state.chatBubbles = action.payload;
    },
    setCtFeedback: (state, action: PayloadAction<string>) => {
      state.ctFeedback = action.payload;
    },
    setFilter: (state, action: PayloadAction<string>) => {
      state.filter = action.payload;
    },
    setSupervisorNotes: (state, action: PayloadAction<string>) => {
      state.supervisorNotes = action.payload;
    },
  },
});

export const {
  setSavedChats,
  setChatBubbles,
  setSelectedChat,
  setCtFeedback,
  setFilter,
  setSupervisorNotes,
} = savedChatsSlice.actions;

export const selectSavedChats = (state: RootState) =>
  state.savedChats.savedChats;

export const selectSelectedChat = (state: RootState) =>
  state.savedChats.selectedChat;

export const selectSavedChatsList = createSelector(selectSavedChats, (map) =>
  Object.values(map).sort((u1, u2) => u1.owner.localeCompare(u2.owner))
);

export const selectChatBubbles = (state: RootState) =>
  state.savedChats.chatBubbles;

export const selectCtFeedback = (state: RootState) =>
  state.savedChats.ctFeedback;

export const selectSavedChatsDto = (state: RootState) =>
  state.savedChats.savedChatsDto;

export const selectFilter = (state: RootState) => state.savedChats.filter;

export const selectSupervisorNotes = (state: RootState) =>
  state.savedChats.supervisorNotes;

export const exportBubblesPlainText = (
  messages: BubbleDto[],
  savedChat: SavedChatDto,
  assistantLabel: string
) => {
  let parts = messages.map(
    (m) =>
      `<<${m.role === 'assistant' ? assistantLabel : savedChat.owner} | ${
        m.original_created_at
      }>>\n${m.content}\n\n`
  );
  parts.push(`****************************************\n\n`);
  parts.push(`Trainee\n${savedChat.owner}\n\n`);
  parts.push(`Training Stage\n${savedChat.stage}\n\n`);
  parts.push(`Reviewer\n${savedChat.reviewer}\n\n`);
  parts.push(`Reviewed Date: \n${savedChat.updated_at}\n\n`);
  parts.push(`Supervisor Notes\n${savedChat.notes}\n\n`);
  parts.push(`CT Feedback\n${savedChat.feedback}\n\n`);
  const file = new Blob(parts, { type: 'text/plain;charset=utf-8' });
  const el = document.createElement('a');
  el.href = URL.createObjectURL(file);
  el.download = `${savedChat.chatName}.txt`;
  document.body.appendChild(el);
  el.click();
};

export const exportBubblesCsv = (
  messages: BubbleDto[],
  assistantLabel: string,
  userLabel: string,
  fileName: string
) => {
  const csvString = [
    ['messageId', 'createdAt', 'role', 'content'],
    ...messages.map((item) => [
      item.messageId,
      item.original_created_at,
      item.role === 'assistant' ? assistantLabel : userLabel,
      item.content
        .replaceAll('\r\n', '')
        .replaceAll('\n', '')
        .replaceAll('\r', '')
        .replaceAll(',', ' '),
    ]),
  ]
    .map((row) => row.join(','))
    .join('\n');

  const file = new Blob([csvString], { type: 'text/csv' });
  const el = document.createElement('a');
  el.href = URL.createObjectURL(file);
  el.download = `${fileName}.csv`;
  document.body.appendChild(el);
  el.click();
};

export default savedChatsSlice.reducer;
